import React, { useState } from 'react';
import { Edit3, Trash2, Clock, Check } from 'lucide-react';
import { getPriorityColor } from '../../utils/taskUtils';
import { formatTime } from '../../utils/dates';
import { useTaskOperations } from '../../hooks/useTaskOperations';

const TaskCard = ({ task, showTimestamp = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ 
    title: task.title, 
    description: task.description || '' 
  });
  const { updateTask, deleteTask, isLoading } = useTaskOperations();

  // NEW: Handle task completion toggle
  const handleToggleComplete = async () => {
    try {
      await updateTask(task.id, { 
        completed: !task.completed,
        completedAt: !task.completed ? new Date() : null
      });
    } catch (error) {
      console.error('Error toggling task completion:', error);
    }
  };

  const handleSave = async () => {
    if (!editData.title.trim()) return;
    
    try {
      await updateTask(task.id, editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await deleteTask(task.id);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleCancel = () => {
    setEditData({ title: task.title, description: task.description || '' });
    setIsEditing(false);
  };

  const isDisabled = task.isAnalyzing || isLoading;

  return (
    <div className={`border-l-4 ${getPriorityColor(task.priorityLevel)} p-4 rounded-lg shadow-sm mb-3 transition-all hover:shadow-md ${
      task.completed ? 'opacity-70 bg-gray-50' : ''
    }`}>
      <div className="flex justify-between items-start mb-2">
        {/* NEW: Completion Checkbox */}
        <div className="flex items-start space-x-3">
          <button
            onClick={handleToggleComplete}
            className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
              task.completed 
                ? 'bg-green-500 border-green-500 text-white hover:bg-green-600' 
                : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
            }`}
            disabled={isDisabled}
            title={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
          >
            {task.completed && <Check className="w-3 h-3" />}
          </button>

          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  value={editData.title}
                  onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full p-2 border rounded-md text-sm font-medium"
                  placeholder="Task title"
                  disabled={isDisabled}
                />
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 border rounded-md text-sm resize-none"
                  rows="2"
                  placeholder="Task description"
                  disabled={isDisabled}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                    disabled={isDisabled || !editData.title.trim()}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* NEW: Strikethrough text for completed tasks */}
                <h3 className={`font-medium text-gray-900 mb-1 transition-all duration-200 ${
                  task.completed ? 'line-through text-gray-600' : ''
                }`}>
                  {task.title}
                </h3>
                {task.description && (
                  <p className={`text-sm text-gray-600 mb-2 transition-all duration-200 ${
                    task.completed ? 'line-through text-gray-500' : ''
                  }`}>
                    {task.description}
                  </p>
                )}
                {task.aiAnalysis?.reasoning && (
                  <div className="text-xs text-gray-500 italic mb-2 p-2 bg-gray-50 rounded">
                    <strong>AI Analysis:</strong> {task.aiAnalysis.reasoning}
                  </div>
                )}
                {/* NEW: Show completion timestamp */}
                {task.completed && task.completedAt && (
                  <div className="text-xs text-green-600 flex items-center">
                    <Check className="w-3 h-3 mr-1" />
                    <span>Completed {formatTime(task.completedAt)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {!isEditing && (
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
              disabled={isDisabled}
              title="Edit task"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
              disabled={isDisabled}
              title="Delete task"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      
      <div className="flex justify-between items-center">
        {/* Priority badge */}
        <div className="flex items-center gap-2">
          {task.priorityLevel && (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              task.priorityLevel === 'high' ? 'bg-red-100 text-red-800' :
              task.priorityLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              {task.priorityLevel.toUpperCase()}
            </span>
          )}
          {/* NEW: Completed badge */}
          {task.completed && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <Check className="w-3 h-3 mr-1" />
              DONE
            </span>
          )}
          {task.isAnalyzing && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              🤖 Analyzing...
            </span>
          )}
        </div>
        
        {showTimestamp && (
          <span className="text-xs text-gray-500 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            {formatTime(task.createdAt)}
          </span>
        )}
      </div>
    </div>
  );
};

export default TaskCard;